import { useLayoutEffect, useRef, useState } from "react";
import ProductImage from "@/store/components/ProductImage";
import PriceTag from "@/store/components/PriceTag";
import BuyButton from "@/store/components/BuyButton";
import PurchasedBadge, { getDescText } from "@/store/components/PurchasedBadge";
import { isBundleCategory, categoryLabel } from "@/store/constants";
import { categoryBadgeColors } from "@/store/categoryColors";
import type { Coupon, Product } from "@/store/types";

interface ProductCardProps {
  product: Product;
  isPurchased: boolean;
  isMember: boolean;
  coupons: Coupon[];
  busy: boolean;
  processingCode: string | null;
  onPurchase: (product: Product) => void;
  onAddToCart: (product: Product) => void;
  onViewDetails: (product: Product) => void;
}

export default function ProductCard({
  product,
  isPurchased,
  isMember,
  coupons,
  busy,
  processingCode,
  onPurchase,
  onAddToCart,
  onViewDetails,
}: ProductCardProps) {
  const descRef = useRef<HTMLDivElement>(null);
  const [truncated, setTruncated] = useState(false);
  const badgeColors = categoryBadgeColors(product.Category);

  // "View details" only appears when the (line-clamped) description is actually cut off.
  useLayoutEffect(() => {
    const el = descRef.current;
    if (el) setTruncated(el.scrollHeight - el.clientHeight > 1);
  }, [product.Description]);

  return (
    <div className="agd:flex agd:flex-col agd:overflow-hidden agd:rounded-[10px] agd:border agd:border-border agd:bg-card agd:shadow-sm agd:transition-shadow hover:agd:shadow-md">
      <div className="agd:px-3 agd:pt-3">
        <span
          className="agd:mb-2 agd:inline-block agd:rounded-md agd:px-2 agd:py-1 agd:text-[0.65rem] agd:font-bold agd:uppercase agd:tracking-wide"
          style={{
            backgroundColor: badgeColors.bg,
            color: badgeColors.text,
          }}
        >
          {categoryLabel(product.Category)}
        </span>
        <ProductImage
          product={product}
          lazy
          className="agd:block agd:h-[120px] agd:w-full agd:rounded-md agd:object-cover"
        />
      </div>
      <div className="agd:flex agd:flex-1 agd:flex-col agd:px-5 agd:pb-5 agd:pt-3.5">
        <div className="agd:mb-1.5 agd:flex agd:items-center agd:justify-between agd:gap-2">
          <span className="agd:text-[0.72rem] agd:font-semibold agd:uppercase agd:tracking-wide agd:text-muted-foreground">
            {product.ProductCode}
          </span>
          <PurchasedBadge show={isPurchased && !isBundleCategory(product.Category)} />
        </div>
        <div className="agd:mb-1.5 agd:text-[0.98rem] agd:font-semibold agd:leading-tight agd:text-foreground">
          {product.Title ?? ""}
        </div>
        <div
          ref={descRef}
          className="agd:mb-3.5 agd:flex-1 agd:overflow-hidden agd:text-[0.82rem] agd:leading-relaxed agd:text-muted-foreground"
          style={{ display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical" }}
        >
          {getDescText(product.Description)}
        </div>
        <div className="agd:mt-auto">
          {truncated ? (
            <button
              type="button"
              className="agd:mb-3 agd:cursor-pointer agd:border-none agd:bg-transparent agd:p-0 agd:text-[0.8rem] agd:font-semibold agd:text-primary hover:agd:underline"
              onClick={() => onViewDetails(product)}
            >
              View details
            </button>
          ) : null}
          <PriceTag product={product} isMember={isMember} />
          <BuyButton
            product={product}
            isPurchased={isPurchased}
            coupons={coupons}
            isMember={isMember}
            busy={busy}
            processing={processingCode === product.ProductCode}
            onPurchase={onPurchase}
            onAddToCart={onAddToCart}
          />
        </div>
      </div>
    </div>
  );
}
